import React from 'react';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';

import { createReaction, deleteReaction } from 'flow/actions/chat-actions';
import Tooltip from 'flow/components/ui-components/Tooltip';
import { getEmojiName } from './reaction-utils';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import { ChatMessageKey, ChatSessionKey } from 'flow/types/EntityKeys';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';

import { twMerge } from 'tailwind-merge';

interface Props {
    /* No hasReacted here on purpose. The API answers it for whoever asked, so
     * it is not a property of the reaction, and a reaction arriving over Top
     * carries no such field at all - a stored one goes undefined the moment
     * somebody else reacts. Who reacted is in `users`; read it from there. */
    data: {
        type: string;
        users: string[];
        count: number;
    }[];
    keys: {
        chatSessionKey: ChatSessionKey;
        chatMessageKey: ChatMessageKey;
        threadParentKey?: ChatMessageKey;
    };
}

const Reactions = ({ data, keys }: Props) => {
    const { i18next } = useAppContext();
    const { openModal } = useModal();
    const dispatch = useAppDispatch();
    const ownExtensionKey = useAppSelector(selectOwnExtensionKey);

    const extensions = useAppSelector((state) =>
        state.extensions.get('extensions'),
    );

    if (!data || data.length === 0) {
        return null;
    }

    const likedByMe = (reaction: string) =>
        !!data
            .find((group) => group.type === reaction)
            ?.users.includes(ownExtensionKey);

    const getUsersReactedLabel = (reaction: string) => {
        const reactions = data.find((group) => group.type === reaction);

        if (!reactions) {
            return;
        }

        const andXMore = reactions.count - reactions.users.length;

        const likedBy = reactions.users
            .filter((extensionKey) => extensionKey !== ownExtensionKey)
            .map(
                (extensionKey) =>
                    extensions.getIn([extensionKey, 'contact', 'firstName']) +
                    ' ' +
                    extensions.getIn([extensionKey, 'contact', 'lastName']),
            );

        if (likedByMe(reaction)) {
            likedBy.unshift(i18next.t('chat.you'));
        }

        return (
            likedBy.join(', ') +
            (andXMore > 0
                ? ` ${i18next.t('reactions.more', { count: andXMore })}`
                : '')
        );
    };

    return (
        <div className="no-select mt-2 flex flex-wrap gap-1">
            {data
                .filter((reaction) => reaction.count > 0)
                .map((reaction) => {
                    const isLikedByMe = likedByMe(reaction.type);
                    return (
                        <div
                            className={twMerge(
                                'flex w-fit items-center justify-center rounded-sm text-xs outline-1',
                                isLikedByMe
                                    ? 'bg-green/10 outline-green'
                                    : 'outline-gray-300',
                            )}
                            key={`${keys.chatMessageKey}-${reaction.type}`}
                        >
                            <button
                                key={`b-${keys.chatMessageKey}-${reaction.type}`}
                                aria-label={i18next.t('action.react_with', {
                                    emoji: getEmojiName(i18next, reaction.type),
                                })}
                                aria-pressed={isLikedByMe}
                                className={twMerge(
                                    'h-full rounded-e-sm ps-1.5 pe-1',
                                    isLikedByMe
                                        ? 'hover:bg-green/20'
                                        : 'hover:bg-gray-200',
                                )}
                                onClick={() => {
                                    const method = isLikedByMe
                                        ? deleteReaction
                                        : createReaction;

                                    const messageReactionSession = {
                                        chatSessionKey: keys.chatSessionKey,
                                        chatMessageKey: keys.chatMessageKey,
                                        shortcode: reaction.type,
                                        threadParentKey: keys?.threadParentKey,
                                    };

                                    dispatch(method(messageReactionSession));
                                }}
                            >
                                <em-emoji shortcodes={reaction.type} />
                            </button>
                            <Tooltip
                                key={`tt-${keys.chatMessageKey}-${reaction.type}`}
                                label={getUsersReactedLabel(reaction.type)}
                                className="whitespace-normal"
                            >
                                <button
                                    className={twMerge(
                                        'h-full rounded-e-sm ps-1 pe-1.5',
                                        isLikedByMe
                                            ? 'hover:bg-green/20'
                                            : 'hover:bg-gray-200',
                                    )}
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        openModal('reactions', {
                                            type: reaction.type,
                                            keys,
                                        });
                                    }}
                                >
                                    {reaction.count}
                                </button>
                            </Tooltip>
                        </div>
                    );
                })}
        </div>
    );
};

export default Reactions;
